import {gsap, TimelineLite} from "gsap";
import {DumpsterFire} from "../../DumpsterFire";
import {ISymbol} from "../../../core/reel/simplereel/IReelData";
import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/reel/winpresentation/SimplePresentationRunner";
import {DumpsterFireSymbol, SYMBOL_NAME} from "../../reels/DumpsterFireSymbol";
import {DumpsterFireGameMode} from "../../server/DumpsterFireGameMode";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {DFSymbolUtils} from "../../utils/DFSymbolUtils";
import {DumpsterFireSound} from "../../sound/DumpsterFireSound";
import {DFGameConfig} from "../../DFGameConfig";

export class DFWinExplodeRunner implements SimplePresentation {
    private _mainTl: TimelineLite;
    private _fourDigitPresent: boolean = false;

    public shouldRun(): boolean {
        return (DumpsterFire.model.gameData.scatterWins.length > 0 || DumpsterFire.model.isXBombPresent) && !((DumpsterFire.model.gameData.scatterWins.length > 0 || DumpsterFire.model.isXBombPresent) && (DumpsterFire.model.data.nextMode === DumpsterFireGameMode.NORMAL || DumpsterFire.model.data.nextMode === DumpsterFireGameMode.EXTRA_SPIN));
    }

    private playAllExplodeSounds(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        DumpsterFire.model.explodeSound.push(this.playExplodeSound());
        DumpsterFire.model.explodeSound.forEach((soundName: string) => {
            tl.add(() => {
                DumpsterFire.soundPlayer.playSound(soundName);
            }, 0);
        });
        return tl;
    }

    private playExplodeSound(): string {
        const random: number = Math.floor(Math.random() * DumpsterFireSound.EXPLODE_SOUND_ARRAY.length);
        return DumpsterFireSound.EXPLODE_SOUND_ARRAY[random];
    }

    public start(controller: SimplePresentationRunnerController): void {
        this._mainTl = new TimelineLite({
            delay: 0.3,
            onStart: () => {
                if (DumpsterFire.model.isXBombPresent) {
                    this._mainTl.add(DumpsterFire.reelAnticipation.hideContainer(), 0)
                }
            },
            onComplete: () => {
                DumpsterFire.model.firstReelCollapse = true;
                DumpsterFire.model.explodeSound = [];
                controller.finish();
            }
        });
        this._mainTl.add(() => {
            controller.setSkipCallback(() => this.onSkip());
        }, 0.001);
        if (DumpsterFire.model.boostedBetName.indexOf("BOOSTED_BET_STATE_") > -1) {
            DumpsterFire.model.isNotNoWinRound = true;
        }
        //Regular Win Symbols
        const explodeTl: TimelineLite = new TimelineLite();
        let isPlayed = false;
        if (DumpsterFire.model.data.scatterWins.length > 0) {
            for (let reelId: number = 0; reelId < DumpsterFire.model.allExplodeWinPositions.length; reelId++) {
                for (let rowId: number = 0; rowId < DumpsterFire.model.allExplodeWinPositions[reelId].length; rowId++) {
                    let symbol: ISymbol = DumpsterFire.allReelsView.getSymbolAtPosition(reelId, rowId);
                    let toSymbol: string = DumpsterFire.model.gameData.evaluatedReels[reelId][rowId];
                    let multiValue: number = DumpsterFire.model.data.positionMultipliers[reelId][rowId];
                    this._fourDigitPresent = multiValue > 512;
                    if (DumpsterFire.model.allExplodeWinPositions[reelId][rowId]) {
                        DumpsterFire.model.symbolCollapsed[reelId] = true;
                        if (!isPlayed) {
                            explodeTl.add(this.playAllExplodeSounds(), 0)
                            isPlayed = true;
                        }
                        explodeTl.add(DumpsterFire.winView.playDisappearAnimation(<DumpsterFireSymbol>symbol, toSymbol), 0);
                        explodeTl.add((DumpsterFire.allReelsView.addSymMulti(DumpsterFire.model.data.positionMultipliers, reelId, rowId)), 0)
                        explodeTl.add(() => {
                            symbol.isRemoved = true;
                            DumpsterFire.allReelsView.mainReelModels[reelId].reelView.removeAvalancheSymbol(symbol);
                        });
                    }
                }
            }
            const innerTl: TimelineLite = new TimelineLite();
            for (let winGroup: number = 0; winGroup < DumpsterFire.model.gameData.scatterWins.length; winGroup++) {
                const groupWin: IBetWayWinData = DumpsterFire.model.gameData.scatterWins[winGroup];
                groupWin.reelWinPositions.forEach((reelWin: boolean[]) => {
                    reelWin.forEach((winPos: boolean) => {
                        if (winPos) {
                            innerTl.add(() => {
                                DumpsterFire.winView.finalWinNumber(groupWin, winGroup);
                            }, 0);
                            return innerTl;
                        }
                    });
                });
            }
            explodeTl.add(innerTl, 0);
            explodeTl.add(() => {
                DumpsterFire.allReelsView.removeAllMulti();
            }, 0.001);
        }

        //xBomb Symbols
        const xBombExplodeTl: TimelineLite = new TimelineLite();
        let firstTime = true;
        if (DumpsterFire.model.isXBombPresent && DumpsterFire.model.data.scatterWins.length === 0) {
            for (let reelId: number = 0; reelId < DumpsterFire.model.data.reels.length; reelId++) {
                for (let rowId: number = 0; rowId < DumpsterFire.model.data.reels[reelId].length; rowId++) {
                    const symbolName: string = DumpsterFire.model.data.reels[reelId][rowId];
                    if (symbolName === "XB") {
                        DumpsterFire.model.symbolCollapsed[reelId] = true;
                        xBombExplodeTl.add([() => {
                            if (firstTime) {
                                DumpsterFire.soundPlayer.playSound(DumpsterFireSound.X_BOMB_EXPLODE);
                                firstTime = false;
                            }
                        },
                            DumpsterFire.winView.xBombAnim(reelId, rowId)], 0);
                        xBombExplodeTl.add([
                            DumpsterFire.reelAnticipation.getAnticipationShake(0.2),
                            DFSymbolUtils.shake(1, 1, 500, 8, 0.1)], 0.3);
                    }
                }
            }
            for (let i: number = 0; i < DumpsterFire.model.explodePositions.length; i++) {
                const reelId: number = Math.floor(DumpsterFire.model.explodePositions[i] / 6),
                    rowId: number = DumpsterFire.model.explodePositions[i] % 6,
                    symbol: ISymbol = DumpsterFire.allReelsView.getSymbolAtPosition(reelId, rowId),
                    toSymbol: string = DumpsterFire.model.gameData.evaluatedReels[reelId][rowId];
                if (reelId < DumpsterFire.model.data.reels.length && rowId < DumpsterFire.model.data.reels[reelId].length) {
                    if (symbol !== undefined && DumpsterFire.model.reels[reelId][rowId] !== SYMBOL_NAME.SCATTER && DumpsterFire.model.reels[reelId][rowId] !== SYMBOL_NAME.WILD) {
                        if (DumpsterFire.model.data.reels[reelId][rowId] !== SYMBOL_NAME.X_BOMB) {
                            let multiValue: number = DumpsterFire.model.data.positionMultipliers[reelId][rowId];
                            this._fourDigitPresent = multiValue > 512;
                            DumpsterFire.model.symbolCollapsed[reelId] = true;
                            xBombExplodeTl.add(() => {
                                DumpsterFire.winView.playDisappearAnimation(<DumpsterFireSymbol>symbol, toSymbol)
                            }, 0.3);
                            xBombExplodeTl.add(() => {
                                DumpsterFire.allReelsView.addSymMulti(DumpsterFire.model.data.positionMultipliers, reelId, rowId)
                            }, 0.3)
                        } else if (DumpsterFire.model.data.reels[reelId][rowId] === SYMBOL_NAME.X_BOMB) {
                            let multiValue: number = DumpsterFire.model.data.positionMultipliers[reelId][rowId];
                            this._fourDigitPresent = multiValue > 512;
                            xBombExplodeTl.add(() => {
                                DumpsterFire.allReelsView.addSymMulti(DumpsterFire.model.data.positionMultipliers, reelId, rowId)
                            }, 0.3)
                        }
                        xBombExplodeTl.add(() => {
                            symbol.isRemoved = true;
                            DumpsterFire.allReelsView.mainReelModels[reelId].reelView.removeAvalancheSymbol(symbol);
                        }, 0.3);
                    }
                }
            }
            xBombExplodeTl.add(() => {
                DumpsterFire.allReelsView.removeAllMulti();
            }, 0.3);

        }
        this._mainTl.add(() => {
            DumpsterFire.soundPlayer.getMultiSound(DumpsterFire.bgMulti.largestMulti());
        }, 0);
        this._mainTl.add(explodeTl, 0);
        this._mainTl.add(xBombExplodeTl, 0);
        this._mainTl.add(() => {
            DumpsterFire.allReelsView.collapse();
            DumpsterFire.winView.removeWinNumbers();
            DumpsterFire.keypadPlugin.setWin(DumpsterFire.model.gameData.accumulatedRoundWin, true, DumpsterFire.isWinBelowStake());
            controller.setSkipCallback();
            DumpsterFire.winView.playNormalAnimationForAll()
        });
    }

    private onSkip(): void {
        this._mainTl.timeScale(4);
    }
}